/**
 * MaoWorkingIndicator - Visual feedback when AI is processing
 *
 * Provides 3 animation variants:
 * 1. dots - Bouncing dots (typing indicator)
 * 2. pulse - Sparkle icon with pulse
 * 3. shimmer - Gradient shimmer bar
 *
 * All animations respect prefers-reduced-motion.
 */

import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { maoWorkingSimple } from '@/lib/mao';
import type { MaoAnimationVariant, MaoSize } from '@/lib/mao';

interface MaoWorkingIndicatorProps {
  /** Animation variant */
  variant?: MaoAnimationVariant;
  /** Custom working text (uses default if not provided) */
  text?: string;
  /** Whether to show text alongside the animation */
  showText?: boolean;
  /** Size variant */
  size?: MaoSize;
  /** Additional CSS classes */
  className?: string;
}

const SIZE_CLASSES: Record<MaoSize, string> = {
  sm: 'text-xs',
  default: 'text-sm',
  lg: 'text-base',
};

export function MaoWorkingIndicator({
  variant = 'pulse',
  text,
  showText = true,
  size = 'default',
  className,
}: MaoWorkingIndicatorProps) {
  const displayText = text || maoWorkingSimple();

  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 text-lavender-600',
        SIZE_CLASSES[size],
        className,
      )}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {variant === 'dots' && <DotsAnimation size={size} />}
      {variant === 'pulse' && <PulseAnimation size={size} />}
      {variant === 'shimmer' && <ShimmerAnimation size={size} />}

      {showText && (
        <span className="font-medium motion-reduce:after:content-['']">
          {displayText}
        </span>
      )}
    </div>
  );
}

/**
 * Bouncing dots animation (typing indicator style)
 */
function DotsAnimation({ size }: { size: MaoSize }) {
  const dotSize =
    size === 'sm' ? 'w-1 h-1' : size === 'lg' ? 'w-2 h-2' : 'w-1.5 h-1.5';

  return (
    <div className="mao-dots flex items-center gap-1" aria-hidden="true">
      <span
        className={cn(
          'rounded-full bg-lavender-500 motion-reduce:animate-none',
          dotSize,
        )}
        style={{ animationDelay: '0ms' }}
      />
      <span
        className={cn(
          'rounded-full bg-lavender-500 motion-reduce:animate-none',
          dotSize,
        )}
        style={{ animationDelay: '160ms' }}
      />
      <span
        className={cn(
          'rounded-full bg-lavender-500 motion-reduce:animate-none',
          dotSize,
        )}
        style={{ animationDelay: '320ms' }}
      />
    </div>
  );
}

/**
 * Sparkle icon with pulse animation
 */
function PulseAnimation({ size }: { size: MaoSize }) {
  const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';

  return (
    <div
      className="mao-pulse motion-reduce:animate-none"
      aria-hidden="true"
    >
      <Sparkles className={cn('text-lavender-500', iconSize)} />
    </div>
  );
}

/**
 * Gradient shimmer bar animation
 */
function ShimmerAnimation({ size }: { size: MaoSize }) {
  const height = size === 'sm' ? 'h-1' : size === 'lg' ? 'h-2' : 'h-1.5';

  return (
    <div
      className={cn(
        'w-16 rounded-full overflow-hidden bg-lavender-200',
        height,
      )}
      aria-hidden="true"
    >
      <div className="mao-shimmer w-full h-full bg-gradient-to-r from-transparent via-lavender-400 to-transparent motion-reduce:hidden" />
    </div>
  );
}
